import { defineStore } from 'pinia';
import { useDocumentStore } from 'src/stores/document';

import { convertBlobToBase64, readAsBase64 } from 'src/util/common/blobHelpers';

import {
	ApplyUploadPatchPayload,
	ApplyUploadProgressPayload,
	EnqueueDocumentUploadsPayload,
	PendingUpload,
	UploadIdPayload,
} from './interfaces';
import createDocumentUploadState, {
	DocumentUploadItem,
	DocumentUploadSource,
	DocumentUploadStateInterface,
} from './state';

const FALLBACK_FILE_MIME_TYPE = 'application/octet-stream';

const createDefaultState = (): DocumentUploadStateInterface => createDocumentUploadState();

const pendingUploads = new Map<string, PendingUpload>();

let uploadIdCounter = 0;

const createUploadId = (): string => {
	uploadIdCounter += 1;

	return `upload-${uploadIdCounter}`;
};

const resolveSourceToBase64 = (source: DocumentUploadSource): Promise<string> => {
	if (source.kind === 'base64') {
		return Promise.resolve(source.data);
	}

	if (source.kind === 'photo') {
		return readAsBase64(source.photo);
	}

	return convertBlobToBase64(source.file);
};

const sourceFileName = (source: DocumentUploadSource): string => {
	if (source.kind === 'base64') {
		return source.name;
	}

	if (source.kind === 'photo') {
		return `${new Date().getTime()}.${source.photo.format}`;
	}

	return source.file.name;
};

const sourceFileType = (source: DocumentUploadSource): string => {
	if (source.kind === 'base64') {
		return source.type;
	}

	if (source.kind === 'photo') {
		return source.photo.format;
	}

	return source.file.type || FALLBACK_FILE_MIME_TYPE;
};

export const useDocumentUploadStore = defineStore('documentUpload', {
	actions: {
		applyUploadPatch(payload: ApplyUploadPatchPayload): void {
			const item = this.uploads.get(payload.id);
			if (item === undefined) {
				return;
			}

			this.uploads.set(payload.id, { ...item, ...payload.patch });
		},
		applyUploadProgress(payload: ApplyUploadProgressPayload): void {
			this.applyUploadPatch({
				id: payload.id,
				patch: {
					phase: payload.percent >= 100 ? 'processing' : 'transferring',
					progress: payload.percent,
				},
			});
		},
		clearFinished(): void {
			this.activeUploads.forEach((item) => {
				if (item.status !== 'succeeded') {
					return;
				}

				pendingUploads.delete(item.id);
				this.uploads.delete(item.id);
			});
		},
		dismiss(payload: UploadIdPayload): void {
			pendingUploads.delete(payload.id);
			this.uploads.delete(payload.id);
		},
		enqueue(payload: EnqueueDocumentUploadsPayload): void {
			payload.sources.forEach((source) => {
				const id = createUploadId();

				pendingUploads.set(id, {
					authenticatedUserId: payload.authenticatedUserId,
					commandBus: payload.commandBus,
					documentBoxId: payload.documentBoxId,
					idoLinkings: payload.idoLinkings,
					source: source,
				});

				this.uploads.set(id, {
					fileName: sourceFileName(source),
					id: id,
					phase: 'preparing',
					progress: 0,
					status: 'pending',
				});
			});

			void this.processQueue();
		},
		async processQueue(): Promise<void> {
			if (this.isProcessing) {
				return;
			}

			this.isProcessing = true;
			try {
				for (;;) {
					const next = Array.from(this.uploads.values()).find((item) => item.status === 'pending');
					if (next === undefined) {
						return;
					}

					await this.processUpload({ id: next.id });
				}
			} finally {
				this.isProcessing = false;
			}
		},
		async processUpload(payload: UploadIdPayload): Promise<void> {
			const item = this.uploads.get(payload.id);
			if (item === undefined) {
				return;
			}

			const pending = pendingUploads.get(payload.id);
			if (pending === undefined) {
				this.applyUploadPatch({ id: payload.id, patch: { status: 'failed' } });

				return;
			}

			this.applyUploadPatch({
				id: payload.id,
				patch: { phase: 'preparing', progress: 0, status: 'running' },
			});

			const documentStore = useDocumentStore();
			let uploadSucceeded = false;
			try {
				const base64FileData = await resolveSourceToBase64(pending.source);

				this.applyUploadPatch({ id: payload.id, patch: { phase: 'transferring' } });

				uploadSucceeded = await documentStore.createTheDocument({
					authenticatedUserId: pending.authenticatedUserId,
					commandBus: pending.commandBus,
					documentBoxId: pending.documentBoxId,
					documentFileData: base64FileData,
					documentFileName: item.fileName,
					documentFileSize: base64FileData.length,
					documentFileTitle: item.fileName,
					documentFileType: sourceFileType(pending.source),
					documentKeyWords: '',
					documentState: 'red',
					idoLinkings: pending.idoLinkings,
					onUploadProgress: (percent: number) =>
						this.applyUploadProgress({ id: payload.id, percent: percent }),
				});
			} catch {
				uploadSucceeded = false;
			}

			if (!uploadSucceeded) {
				this.applyUploadPatch({ id: payload.id, patch: { status: 'failed' } });

				return;
			}

			pendingUploads.delete(payload.id);
			this.applyUploadPatch({
				id: payload.id,
				patch: { phase: 'processing', progress: 100, status: 'succeeded' },
			});
			this.completedCount += 1;
		},
		retry(payload: UploadIdPayload): void {
			const item = this.uploads.get(payload.id);
			if (item === undefined || !pendingUploads.has(payload.id)) {
				return;
			}

			this.applyUploadPatch({ id: payload.id, patch: { progress: 0, status: 'pending' } });
			void this.processQueue();
		},
	},
	getters: {
		activeUploads(): Array<DocumentUploadItem> {
			return Array.from(this.uploads.values());
		},
		failedCount(): number {
			return this.activeUploads.filter((item) => item.status === 'failed').length;
		},
		hasFailedUploads(): boolean {
			return this.activeUploads.some((item) => item.status === 'failed');
		},
		hasVisibleUploads(): boolean {
			return this.uploads.size > 0;
		},
		overallProgress(): number {
			if (this.totalCount === 0) {
				return 0;
			}

			const collected = this.activeUploads.reduce((total, item) => {
				if (item.status === 'succeeded') {
					return total + 100;
				}

				return item.status === 'running' ? total + item.progress : total;
			}, 0);

			return Math.round(collected / this.totalCount);
		},
		runningIndex(): number {
			return this.activeUploads.filter((item) => item.status !== 'pending').length;
		},
		totalCount(): number {
			return this.uploads.size;
		},
	},
	state: createDefaultState,
});

export * from './interfaces';
export * from './types';
